import { useEffect } from "react";
import { IoAnalyticsOutline } from "react-icons/io5";

import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import MarkdownRenderer from "components/MarkdownRenderer";

import { useAnalytics } from "context/AnalyticsContext";
import useAIReportData from "./data/useAIReportData";
import { analyticsService } from "services/trackingService";
import { generateReportPDF } from "utils/pdfGenerator";

// ── Colours ───────────────────────────────────────────────────────────────────
const C = {
  cyan:   "#2CD9FF",
  red:    "#f87171",
  amber:  "#fbbf24",
  green:  "#34d399",
  blue:   "#60a5fa",
  purple: "#a78bfa",
};

function threatColour(value) {
  const v = (value || "").toLowerCase();
  if (v.startsWith("high"))   return C.red;
  if (v.startsWith("medium")) return C.amber;
  if (v.startsWith("low"))    return C.green;
  return "rgba(255,255,255,0.55)";
}

// ── Skeleton placeholder ──────────────────────────────────────────────────────
function Skeleton({ height = 80, width = "100%" }) {
  return (
    <VuiBox sx={{ height, width, background: "rgba(255,255,255,0.05)", borderRadius: 1.5, animation: "pulse 1.5s ease-in-out infinite", "@keyframes pulse": { "0%,100%": { opacity: 0.5 }, "50%": { opacity: 1 } } }} />
  );
}

// ── Highlight minicards ───────────────────────────────────────────────────────
function HighlightCards({ items, loading }) {
  if (!items) return null;
  return (
    <VuiBox display="flex" flexWrap="wrap" gap={1.5} mb={2.5}>
      {items.map(({ label, value, coloured }) => {
        const colour = coloured ? threatColour(value) : null;
        return (
          <VuiBox
            key={label}
            sx={{
              flex: "1 1 140px", minWidth: 120,
              background: colour ? `${colour}14` : "rgba(255,255,255,0.04)",
              border: `1px solid ${colour ? `${colour}30` : "rgba(255,255,255,0.09)"}`,
              borderRadius: 2, px: 2, py: 1.75,
            }}
          >
            <VuiTypography sx={{ display: "block", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.09em", color: "rgba(255,255,255,0.5)", mb: 0.75 }}>
              {label}
            </VuiTypography>
            {loading
              ? <Skeleton height={16} width="60%" />
              : <VuiTypography sx={{ fontWeight: 700, fontSize: "0.9rem", color: colour || "#f1f5f9", lineHeight: 1.25 }}>
                  {value || "—"}
                </VuiTypography>
            }
          </VuiBox>
        );
      })}
    </VuiBox>
  );
}




// ── Standard section card ─────────────────────────────────────────────────────
function SectionCard({ label, content, accent, loading, stripCompany = false }) {
  return (
    <VuiBox sx={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.09)", borderTop: `3px solid ${accent}`, borderRadius: 2, p: 3, mb: 2 }}>
      <VuiTypography sx={{ display: "block", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: accent, mb: 1.75 }}>
        {label}
      </VuiTypography>
      {loading
        ? <><Skeleton height={14} width="90%" /><Skeleton height={14} width="75%" sx={{ mt: 1 }} /><Skeleton height={14} width="80%" sx={{ mt: 1 }} /></>
        : content
          ? <MarkdownRenderer markdownText={content} stripCompany={stripCompany} />
          : <VuiTypography sx={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>No data this period.</VuiTypography>
      }
    </VuiBox>
  );
}

// ── Action card — visually distinct for recommendations ───────────────────────
function ActionCard({ label, content, accent, loading }) {
  return (
    <VuiBox sx={{ flex: "1 1 300px", background: `${accent}08`, border: `1px solid ${accent}22`, borderTop: `3px solid ${accent}`, borderRadius: 2, p: 3 }}>
      <VuiTypography sx={{ display: "block", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: accent, mb: 1.75 }}>
        {label}
      </VuiTypography>
      {loading
        ? <><Skeleton height={14} width="85%" /><Skeleton height={14} width="70%" sx={{ mt: 1 }} /></>
        : content
          ? <MarkdownRenderer markdownText={content} />
          : <VuiTypography sx={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.3)", fontStyle: "italic" }}>No data this period.</VuiTypography>
      }
    </VuiBox>
  );
}

// ── Two-column pair ───────────────────────────────────────────────────────────
function SectionPair({ left, right, loading, action = false }) {
  const Card = action ? ActionCard : SectionCard;
  return (
    <VuiBox display="flex" gap={2} flexWrap="wrap" mb={2}>
      <VuiBox sx={{ flex: "1 1 300px" }}><Card {...left} loading={loading} /></VuiBox>
      <VuiBox sx={{ flex: "1 1 300px" }}><Card {...right} loading={loading} /></VuiBox>
    </VuiBox>
  );
}

// ── Highlight item builders ───────────────────────────────────────────────────
const competitorItems = (h) => !h ? null : [
  { label: "Threat Level",       value: h.threatLevelBadge,  coloured: true  },
  { label: "Active Trials",      value: h.activeTrialsCount, coloured: false },
  { label: "Top Content Theme",  value: h.topContentTheme,   coloured: false },
  { label: "Hiring Signal",      value: h.hiringSignal,      coloured: false },
  { label: "Report Period",      value: h.reportPeriod,      coloured: false },
];

const marketItems = (h) => !h ? null : [
  { label: "Most Threatening",   value: h.mostThreateningCompetitor, coloured: false },
  { label: "Dominant Theme",     value: h.dominantMarketTheme,       coloured: false },
  { label: "Active Trials",      value: h.totalActiveTrials,         coloured: false },
  { label: "Market Hiring",      value: h.marketHiringPosture,       coloured: false },
];

// ── Main page ─────────────────────────────────────────────────────────────────
function AIReportsPage() {
  const { selectedCompetitor, setSelectedCompetitor, competitors } = useAnalytics();

  useEffect(() => { setSelectedCompetitor("market_overview"); }, []); // eslint-disable-line

  const { report, loading, error } = useAIReportData(selectedCompetitor);

  useEffect(() => {
    if (selectedCompetitor) analyticsService.trackPageView("AI_REPORTS", { companyId: selectedCompetitor });
  }, [selectedCompetitor]);

  const isMarket       = selectedCompetitor === "market_overview";
  const competitorName = !isMarket ? competitors?.find(c => c.id === selectedCompetitor)?.name || "" : "";
  const period         = report?.windowStart ? `${report.windowStart.slice(0,7)} – ${report.windowEnd?.slice(0,7)}` : "";
  const subtitle       = isMarket
    ? `Market-wide competitive intelligence${period ? ` · ${period}` : ""}`
    : `${competitorName}${period ? ` · ${period}` : ""}`;

  const highlights = isMarket ? marketItems(report?.highlights) : competitorItems(report?.highlights);

  return (
    <DashboardLayout>
      <DashboardNavbar onDownloadReport={(!loading && report) ? () => generateReportPDF(report, competitors, selectedCompetitor) : null} />
      <VuiBox px={2} py={1}>

        {/* Page header */}
        <VuiBox mb={3}>
          <VuiBox display="flex" alignItems="center" gap={1.5} mb={0.5}>
            <IoAnalyticsOutline size={22} color={C.cyan} />
            <VuiTypography variant="h4" color="white" fontWeight="bold">
              {isMarket ? "Market Overview" : "Competitor Report"}
            </VuiTypography>
          </VuiBox>
          <VuiTypography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
            {subtitle}
          </VuiTypography>
        </VuiBox>

        {/* Error */}
        {error && (
          <VuiBox sx={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 2, p: 3, textAlign: "center" }}>
            <VuiTypography variant="body2" color="error">Failed to load report. Please try again.</VuiTypography>
          </VuiBox>
        )}

        {/* Empty */}
        {!loading && !error && !report && (
          <VuiBox sx={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 2, p: 6, textAlign: "center" }}>
            <IoAnalyticsOutline size={40} color="rgba(255,255,255,0.12)" />
            <VuiTypography variant="h6" color="text" mt={2} mb={1}>No report available</VuiTypography>
            <VuiTypography sx={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.4)" }}>
              Trigger report generation from the Admin panel.
            </VuiTypography>
          </VuiBox>
        )}

        {/* Report */}
        {(loading || (!error && report)) && (
          <>
            {/* Highlights */}
            <HighlightCards items={highlights || (isMarket ? marketItems({}) : competitorItems({}))} loading={loading} />

            {isMarket ? (
              <>
                {/* What shifted */}
                <SectionCard label="Momentum Signals"      content={report?.momentumSignals}      accent={C.amber}  loading={loading} />

                {/* Competitive picture */}
                <SectionCard label="Intelligence Brief"    content={report?.intelligenceBrief}    accent={C.cyan}   loading={loading} stripCompany />
                <SectionCard label="Competitive Landscape" content={report?.competitiveLandscape}  accent={C.green}  loading={loading} />
                <SectionPair
                  loading={loading}
                  left={  { label: "Threat Assessment", content: report?.threatAssessment, accent: C.red  }}
                  right={ { label: "Your Position",     content: report?.yourPosition,     accent: C.blue }}
                />

                {/* Actions */}
                <ActionCard label="Recommendations" content={report?.recommendations} accent={C.purple} loading={loading} />
              </>
            ) : (
              <>
                {/* Intelligence */}
                <SectionCard label="Intelligence Brief"   content={report?.intelligenceBrief}   accent={C.cyan}  loading={loading} stripCompany />
                <SectionCard label="Content Intelligence" content={report?.contentIntelligence} accent={C.amber} loading={loading} />

                {/* Actions — highest value first */}
                <SectionPair
                  action
                  loading={loading}
                  left={  { label: "Marketing", content: report?.recommendationsMarketing, accent: C.green }}
                  right={ { label: "Sales",     content: report?.recommendationsSales,     accent: C.blue  }}
                />

                {/* Strategic context */}
                <SectionCard label="Strategic Analysis" content={report?.strategicAnalysis} accent={C.red} loading={loading} />
              </>
            )}
          </>
        )}
      </VuiBox>
    </DashboardLayout>
  );
}

export default AIReportsPage;
